import {
  getWorkflowRuleGroups,
  toWorkflowRuleGroupsPayload,
  workflowRuleGroupsSchema,
  canPreviewWorkflowRuleGroups,
} from "./workflow-rule-groups.ts";
import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import { InputWithVariableInsert } from "@/components/input-with-variable-insert/InputWithVariableInsert.tsx";
import { getWorkflowPlaceholderOptions } from "@/components/rich-editor/rich-editor.types.ts";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { WorkflowReminderSettingsSection } from "@/pages/workflows/edit/actions/WorkflowReminderSettingsSection.tsx";
import { useWorkflowBlockConditions } from "@/pages/workflows/edit/flow/use-workflow-block-conditions.tsx";
import { WorkflowBlockConditionsList } from "@/pages/workflows/edit/flow/WorkflowBlockConditionsList.tsx";
import { useSurveyTemplateSearch } from "@/queries/surveys/use-survey-templates.query.ts";
import { useWorkflowUpdateBlockActionSurvey } from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiFormTemplateFilter } from "@/types/forms/form-template.types.ts";
import { ApiNotificationChannelType } from "@/types/notification.types.ts";
import { ReminderMode, ReminderPeriod, ReminderSettings } from "@/types/reminder.types.ts";
import { RuleGroup } from "@/types/rule.types.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import { useRuleConditionTypes } from "@/queries/use-rules.query.ts";
import {
  ApiWorkflowBlockActionSurvey,
  ApiWorkflowBlockActionSurveySave,
  ApiWorkflowSurveyPeriodType,
  getWorkflowSurveyPeriodItems,
} from "@/types/workflows/workflow.action-notification.types.ts";
import { ApiWorkflowBlockActionTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowBlock, ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Input,
  Modal,
  ModalProps,
  Select,
  Stack,
  Switch,
  TabItem,
  Tabs,
  Textarea,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { WorkflowActionTimingEditor } from "@/pages/workflows/edit/actions/WorkflowActionTimingEditor.tsx";
import {
  getWorkflowActionTimingPayload,
  getWorkflowActionTimingValues,
  useWorkflows,
  WorkflowActionTimingFormFields,
} from "@/pages/workflows/workflow-hook.tsx";

export interface WorkflowEditActionSurveyModalProps extends ModalProps {
  workflowVersion: ApiWorkflowVersion;
  block: ApiWorkflowBlock;
  actionSurvey: ApiWorkflowBlockActionSurvey;
}

interface WorkflowEditActionSurveyBlockData {
  title: string;
  description: string | null;
  form_template_id: number;
  emoji: string | null;
  use_ai: boolean;
  is_anonymous: boolean;
  timeframe_period_value: number;
  timeframe_period_type: ApiWorkflowSurveyPeriodType;
  participants_rule_groups: RuleGroup[];
  share_employee_rule_groups: RuleGroup[];
  invitation_subject: string | null;
  invitation_body: string | null;
}

interface WorkflowEditActionSurveyFormData extends WorkflowActionTimingFormFields {
  title: string | null;
  reminders_enabled: boolean;
  reminder_settings: ReminderSettings;
  block_data: WorkflowEditActionSurveyBlockData;
}

export const WorkflowEditActionSurveyModal = ({
  workflowVersion,
  block,
  actionSurvey,
  ...props
}: WorkflowEditActionSurveyModalProps) => {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();
  const participantConditionTypes = useRuleConditionTypes(
    RuleRegistryPlace.workflowSurveyParticipants
  );
  const shareEmployeeConditionTypes = useRuleConditionTypes(
    RuleRegistryPlace.workflowSurveyShareEmployees
  );
  const { workflowActionTimingSchemaFields } = useWorkflows();

  const [useEmoji, setUseEmoji] = useState(!!actionSurvey.emoji);

  const { conditions, handleAddCondition, handleEditCondition, handleDeleteCondition } =
    useWorkflowBlockConditions(workflowVersion, block);

  const defaultReminderSettings: ReminderSettings = {
    channels: [
      ApiNotificationChannelType.in_app,
      ApiNotificationChannelType.email,
      ApiNotificationChannelType.slack,
    ],
    mode: ReminderMode.medium,
    custom_subject: null,
    custom_body: null,
    custom_frequency: null,
    custom_period: null,
  };

  const WorkflowEditActionSurveySchemaObject = yup.object({
    title: yup.string().defined().nullable(),
    ...workflowActionTimingSchemaFields,
    reminders_enabled: yup.boolean().required(),
    reminder_settings: yup.object({
      channels: yup
        .array()
        .of(
          yup
            .mixed<ApiNotificationChannelType>()
            .oneOf(Object.values(ApiNotificationChannelType))
            .required()
        )
        .default(defaultReminderSettings.channels),
      mode: yup
        .mixed<ReminderMode>()
        .oneOf(Object.values(ReminderMode))
        .required()
        .default(ReminderMode.medium),
      custom_subject: yup.string().nullable().default(null),
      custom_body: yup.string().nullable().default(null),
      custom_frequency: yup.number().nullable().default(null),
      custom_period: yup
        .mixed<ReminderPeriod>()
        .oneOf(Object.values(ReminderPeriod))
        .nullable()
        .default(null),
    }),
    block_data: yup.object({
      title: yup.string().required(t("errors:field_required")),
      description: yup.string().defined().nullable(),
      emoji: yup.string().defined().nullable(),
      form_template_id: yup.number().required(t("errors:field_required")),
      use_ai: yup.boolean().required(),
      is_anonymous: yup.boolean().required(),
      timeframe_period_type: yup
        .mixed<ApiWorkflowSurveyPeriodType>()
        .oneOf(Object.values(ApiWorkflowSurveyPeriodType))
        .required(t("errors:field_required")),
      timeframe_period_value: yup
        .number()
        .required(t("errors:field_required"))
        .typeError(t("errors:field_number_required")),
      invitation_subject: yup.string().defined().nullable(),
      invitation_body: yup.string().defined().nullable(),
      participants_rule_groups: workflowRuleGroupsSchema(),
      share_employee_rule_groups: workflowRuleGroupsSchema(),
    }),
  });

  const {
    control,
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm<WorkflowEditActionSurveyFormData>({
    mode: "onSubmit",
    resolver: yupResolver(WorkflowEditActionSurveySchemaObject),
    values: {
      title: block.title ?? "",
      ...getWorkflowActionTimingValues(block),
      reminders_enabled: !!actionSurvey.reminder_settings?.mode,
      reminder_settings: actionSurvey.reminder_settings?.mode
        ? (actionSurvey.reminder_settings as ReminderSettings)
        : defaultReminderSettings,
      block_data: {
        title: actionSurvey.title,
        description: actionSurvey.description,
        emoji: actionSurvey.emoji || emojiDefaults.survey.default,
        form_template_id: actionSurvey.form_template_id,
        use_ai: actionSurvey.use_ai,
        is_anonymous: actionSurvey.is_anonymous,
        timeframe_period_type: actionSurvey.timeframe_period_type,
        timeframe_period_value: actionSurvey.timeframe_period_value,
        invitation_subject: actionSurvey.invitation_subject,
        invitation_body: actionSurvey.invitation_body,
        participants_rule_groups: getWorkflowRuleGroups(
          actionSurvey.participants_rule_groups,
          actionSurvey.participants_rules
        ),
        share_employee_rule_groups: getWorkflowRuleGroups(
          actionSurvey.share_employee_rule_groups,
          actionSurvey.share_employee_rules
        ),
      },
    },
  });

  const timeframePeriodValue = useWatch({ control, name: "block_data.timeframe_period_value" });
  const hasTimeframe = !!timeframePeriodValue && timeframePeriodValue > 0;

  const watchedBlockData = useWatch({ control, name: "block_data" });

  const updateBlockAction = useWorkflowUpdateBlockActionSurvey(control);

  const [templateFilter, setTemplateFilter] = useState<ApiFormTemplateFilter>({
    page_number: 1,
    text: "",
  });

  const { data: surveyTemplates, isLoading: isTemplatesLoading } = useSurveyTemplateSearch(
    templateFilter,
    true
  );

  const templateOptions = useMemo(() => {
    return (
      surveyTemplates?.items.map((template) => ({
        id: template.id,
        title: template.title,
      })) ?? []
    );
  }, [surveyTemplates]);

  const handleSave = handleSubmit(async (data) => {
    try {
      const reminderSettingsPayload =
        data.reminders_enabled && data.reminder_settings
          ? {
              channels: data.reminder_settings.channels,
              custom_subject: data.reminder_settings.custom_subject,
              custom_body: data.reminder_settings.custom_body,
              mode: data.reminder_settings.mode,
              ...(data.reminder_settings.mode === ReminderMode.custom
                ? {
                    custom_frequency: data.reminder_settings.custom_frequency,
                    custom_period: data.reminder_settings.custom_period,
                  }
                : {}),
            }
          : null;

      const blockData: ApiWorkflowBlockActionSurveySave = {
        title: data.block_data.title,
        description: data.block_data.description,
        emoji: useEmoji ? ":memo:" : null,
        form_template_id: data.block_data.form_template_id,
        use_ai: data.block_data.use_ai,
        is_anonymous: data.block_data.is_anonymous,
        timeframe_period_type: data.block_data.timeframe_period_type,
        timeframe_period_value: data.block_data.timeframe_period_value,
        invitation_subject: data.block_data.invitation_subject,
        invitation_body: data.block_data.invitation_body,
        participants_rule_groups: toWorkflowRuleGroupsPayload(
          data.block_data.participants_rule_groups
        ),
        share_employee_rule_groups: toWorkflowRuleGroupsPayload(
          data.block_data.share_employee_rule_groups
        ),
        reminder_settings: reminderSettingsPayload,
      };

      await updateBlockAction.mutateAsync({
        workflowId: workflowVersion.workflow_id,
        workflowVersionId: workflowVersion.id,
        workflowBlockId: block.id,
        payload: {
          title: data.title,
          ...getWorkflowActionTimingPayload(data),
          block_data: blockData,
          conditions: conditions.map((x) => ({ ...x })),
        },
      });

      analyticsService.trackEvent(analyticEvents.workflows.blockEdited, {
        [analyticProperties.id]: workflowVersion.workflow_id,
        [analyticProperties.actionType]: ApiWorkflowBlockActionTypes.survey,
      });

      close(id);
    } catch {
      // handle error
    }
  });

  const handleTabChanged = (tabIndex: number) => {
    setActiveTab(tabIndex);
  };

  const [activeTab, setActiveTab] = useState(0);

  return (
    <>
      <Modal
        {...props}
        variant={"side"}
        title={t("workflow:action_type_survey")}
        layout={"stretch"}
        withCloser
        closeByEscEnabled
        size={"lg"}
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              size={"lg"}
              className={"w-full"}
              type={"submit"}
              onClick={handleSave}
              isLoading={updateBlockAction.isPending}
            >
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={handleSave}>
          <Tabs onActiveTabChange={handleTabChanged} activeTab={activeTab}>
            <TabItem title={t("surveys:survey")}>
              <Stack className={"py-6"}>
                <Stack>
                  <Title header={t("common:details")} paddingBottom />
                  <Input
                    label={t("common:title")}
                    type="text"
                    {...register("block_data.title")}
                    required={true}
                    error={errors.block_data?.title?.message}
                  />
                  <Textarea
                    label={t("common:description")}
                    {...register("block_data.description")}
                    error={errors.block_data?.description?.message}
                  />
                  <Stack gap={"md"}>
                    <Controller
                      control={control}
                      name={"block_data.form_template_id"}
                      render={({ field: { value, onChange } }) => (
                        <Select
                          label={t("surveys:survey_template")}
                          emptyTitle={t("common:select")}
                          value={value}
                          required={true}
                          searchable
                          isLoading={isTemplatesLoading}
                          options={templateOptions}
                          loadOptionsOnOpen
                          searchOptions={templateOptions}
                          onSearchOptions={(text) => {
                            setTemplateFilter({ ...templateFilter, text });
                          }}
                          onChange={onChange}
                          error={errors.block_data?.form_template_id?.message}
                        />
                      )}
                    />

                    <Switch
                      size={"sm"}
                      checked={!useEmoji}
                      onChange={(value) => {
                        setUseEmoji(!value);
                      }}
                      title={t("surveys:do_not_use_emoji")}
                    />

                    <Controller
                      control={control}
                      name={"block_data.use_ai"}
                      render={({ field: { value, onChange } }) => (
                        <Switch
                          size={"sm"}
                          checked={value}
                          onChange={onChange}
                          title={t("surveys:use_ai_title")}
                          description={t("surveys:use_ai_description")}
                        />
                      )}
                    />

                    <Controller
                      control={control}
                      name={"block_data.is_anonymous"}
                      render={({ field: { value, onChange } }) => (
                        <Switch
                          size={"sm"}
                          checked={value}
                          onChange={onChange}
                          title={t("surveys:make_anonymous")}
                        />
                      )}
                    />
                  </Stack>
                </Stack>

                <Stack>
                  <Title header={t("common:timeframe")} paddingBottom paddingTop />
                  <Stack gap={"md"} direction={"horizontal"}>
                    <Input
                      label={t("common:amount")}
                      type="number"
                      min={0}
                      {...register("block_data.timeframe_period_value", { valueAsNumber: true })}
                      required={true}
                      error={errors.block_data?.timeframe_period_value?.message}
                    />
                    <Box className={"grow"}>
                      <Controller
                        control={control}
                        name={"block_data.timeframe_period_type"}
                        render={({ field: { value, onChange } }) => (
                          <Select
                            label={t("common:period")}
                            emptyTitle={t("common:select")}
                            value={value}
                            required={true}
                            options={getWorkflowSurveyPeriodItems()}
                            onChange={onChange}
                            error={errors.block_data?.timeframe_period_type?.message}
                          />
                        )}
                      />
                    </Box>
                  </Stack>
                </Stack>

                <Stack>
                  <Title header={t("common:invitation")} paddingTop paddingBottom />
                  <InputWithVariableInsert
                    label={t("common:invitation_subject")}
                    type="text"
                    {...register("block_data.invitation_subject")}
                    error={errors.block_data?.invitation_subject?.message}
                    variableOptions={getWorkflowPlaceholderOptions(
                      workflowVersion.trigger_event?.event_type
                    )}
                  />
                  <Stack>
                    <Title
                      header={t("workflow:action_type_invitation_body")}
                      size={"xs"}
                      paddingTop
                      paddingBottom
                    />
                    <Controller
                      control={control}
                      name={"block_data.invitation_body"}
                      render={({ field: { value, onChange } }) => (
                        <RichEditor
                          value={value ?? ""}
                          placeholder={t("core:enter_invitation_body_text") + "..."}
                          onChange={onChange}
                          minHeight={100}
                          error={errors.block_data?.invitation_body?.message}
                          placeholderOptions={getWorkflowPlaceholderOptions(
                            workflowVersion.trigger_event?.event_type
                          )}
                        />
                      )}
                    />
                  </Stack>
                </Stack>

                <Title header={t("common:participants")} paddingBottom paddingTop size={"xs"} />
                <RuleGroupsEditor
                  control={control}
                  fieldName={"block_data.participants_rule_groups"}
                  setValue={setValue}
                  place={RuleRegistryPlace.workflowSurveyParticipants}
                  countEmployees={canPreviewWorkflowRuleGroups(
                    watchedBlockData?.participants_rule_groups,
                    participantConditionTypes.conditionTypeInfos
                  )}
                  conditionTypes={participantConditionTypes.conditionTypes}
                  variant="list"
                />
                <Title
                  header={t("surveys:share_specific_people_title")}
                  paddingBottom
                  paddingTop
                  size={"xs"}
                />
                <RuleGroupsEditor
                  control={control}
                  fieldName={"block_data.share_employee_rule_groups"}
                  setValue={setValue}
                  place={RuleRegistryPlace.workflowSurveyShareEmployees}
                  countEmployees={canPreviewWorkflowRuleGroups(
                    watchedBlockData?.share_employee_rule_groups,
                    shareEmployeeConditionTypes.conditionTypeInfos
                  )}
                  conditionTypes={shareEmployeeConditionTypes.conditionTypes}
                  variant="list"
                />

                <Stack gap={"md"}>
                  <Title header={t("core:notifications_and_reminders")} paddingBottom paddingTop />
                  <WorkflowReminderSettingsSection
                    control={control}
                    setValue={setValue}
                    hasSmartBase={hasTimeframe}
                    smartBaseValue={timeframePeriodValue ?? 0}
                    requiredText={t("core:reminder_requires_period")}
                    renderHeader={({ checked, onChange }) => (
                      <Switch
                        size={"sm"}
                        checked={checked}
                        onChange={onChange}
                        title={t("core:turn_on_reminders")}
                        description={t("tasks:overdue_alerts_sent_anyway")}
                      />
                    )}
                  />
                </Stack>
              </Stack>
            </TabItem>
            <TabItem title={t("workflow:conditions")}>
              <Stack gap={"lg"} className={"py-6"}>
                <Stack>
                  <Title header={t("workflow:block")} size={"xs"} />
                  <Input
                    label={t("common:title")}
                    type="text"
                    {...register("title")}
                    error={errors.title?.message}
                  />
                </Stack>
                <WorkflowActionTimingEditor
                  control={control}
                  register={register}
                  errors={errors}
                  triggerDateAvailable={workflowVersion.trigger_schedule_rule !== null}
                />
                <WorkflowBlockConditionsList
                  conditions={conditions}
                  onAdd={handleAddCondition}
                  onEdit={handleEditCondition}
                  onDelete={handleDeleteCondition}
                />
              </Stack>
            </TabItem>
          </Tabs>
        </form>
      </Modal>
    </>
  );
};
